import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from '@src/utils/day';
import { useDispatch, useSelector } from 'react-redux';
import { setAuthorization } from '@src/services/api';
import {
  getCurrentUser,
  getUserAuthorizationStatus,
  getUserAuthorizationStatusGift,
  getUserAuthorizationStatusMarket,
} from '@src/store/slice/userSlice';
import { continueRefreshToken } from '@src/store/slice/authSlice';
import useInterval from '@src/hooks/useInterval';
import { persistLogin } from '@src/utils/utils';
import useMouseInactiveTime from '@src/hooks/useMouseEvent';
import { confirmDialog } from '@src/components';
import { userCustomersGetList } from '@src/store/slice/customerSlice';
import {
  getPendingNotifications,
  markNotificationsStale,
} from '@src/store/slice/notificationSlice';
import { Text as CustomText } from '@src/components';
import { useLang } from '@src/context/LanguageContext';

const UserType = {
  ADMIN: 3,
};

function mouseInactiveTimePopup(
  inActiveTime,
  forceLoginPopupOpened,
  dispatch,
  setForceLoginPopupOpened,
) {
  if (!inActiveTime) return;
  if (forceLoginPopupOpened) return;
  confirmDialog({
    title: <CustomText t="attention" />,
    message: <CustomText t="session.inactive" />,
    okText: <CustomText t="loginButton" />,
    onOk: async () => {
      await persistLogin(dispatch, false);
    },
    okCancel: false,
    closeIcon: false,
  });
  setForceLoginPopupOpened(true);
}

async function checkRefreshTokenPopup(
  loginWithOutRefreshToken,
  expirationTime,
  dispatch,
  refreshToken,
  history,
) {
  if (!loginWithOutRefreshToken) return;
  const _expirationTime = dayjs(expirationTime);
  const _now = dayjs();
  const diffInSecond = _expirationTime.diff(_now, 'seconds');
  if (diffInSecond < 120 && diffInSecond > -10) {
    const action = await dispatch(continueRefreshToken(refreshToken));
    if (continueRefreshToken.rejected.match(action)) {
      setAuthorization(``);
      history.push('/login', { from: history.location });
      await persistLogin(dispatch, false);
    }
  }
}

function checkAuth(token, history, dispatch, setIsInitialize) {
  if (!token) {
    history.push('/login');
    return;
  }
  setAuthorization(`Bearer ${token}`);
  setTimeout(async () => {
    const user = await dispatch(getCurrentUser());
    if (getCurrentUser.rejected.match(user)) {
      setAuthorization(``);
      history.push('/login', { from: history.location });
      await persistLogin(dispatch, false);
      return;
    }
    if (getCurrentUser.fulfilled.match(user)) {
      await dispatch(
        userCustomersGetList({
          userId: user.payload.data.id,
        }),
      );
    }
    await dispatch(getUserAuthorizationStatus());
    await dispatch(getUserAuthorizationStatusGift());
    await dispatch(getUserAuthorizationStatusMarket());
    if (user.payload.data.adminTypeId !== UserType.ADMIN) {
      dispatch(getPendingNotifications({}));
    }
    setIsInitialize(true);
  }, 50);
}

export const useAuthLogic = () => {
  const { token, refreshToken, expirationTime, loginWithOutRefreshToken, isAdmin } = useSelector(
    (state) => state?.auth,
  );
  const dispatch = useDispatch();
  const history = useHistory();
  const [isInitialize, setIsInitialize] = useState(false);
  const inActiveTime = useMouseInactiveTime();
  const [forceLoginPopupOpened, setForceLoginPopupOpened] = useState(false);
  const [loginControl, setLoginControl] = useState(0);
  const { language } = useLang();
  const isFirstLanguageRender = useRef(true);

  useEffect(() => {
    mouseInactiveTimePopup(inActiveTime, forceLoginPopupOpened, dispatch, setForceLoginPopupOpened);
  }, [inActiveTime, forceLoginPopupOpened]);

  useInterval(async () => {
    await checkRefreshTokenPopup(
      loginWithOutRefreshToken,
      expirationTime,
      dispatch,
      refreshToken,
      history,
    );
  }, 10000);

  useEffect(async () => {
    checkAuth(token, history, dispatch, setIsInitialize);
  }, [dispatch, loginControl]);

  useEffect(() => {
    setTimeout(() => {
      setLoginControl((value) => value + 1);
    }, 1000);
  }, [token]);

  // Switching the language must not fire the pending-notification requests from every screen the
  // user happens to be on - the dashboard refetches them itself, and the header bell refetches
  // them when it is opened. Here we only flag the already loaded list as belonging to the previous
  // language.
  useEffect(() => {
    if (isFirstLanguageRender.current) {
      isFirstLanguageRender.current = false;
      return;
    }
    if (isInitialize && !isAdmin) {
      dispatch(markNotificationsStale());
    }
  }, [language]);

  return {
    isInitialize,
  };
};
